import { Component, NgZone, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  FormComponent,
  FormHandlingComponent,
  FormInput,
  Page, PageComponent,
  Redirection,
  Transfer
} from '@tytapp/common';
import { CreateStripeTokenResponse, StripeService } from '@tytapp/stripe';
import { UserService } from '@tytapp/user';
import { ApiMembership, ApiMembershipProduct, ApiOffer, ApiOfferSet, ApiUser, OffersApi, UsersApi } from 'api';
import { environment } from 'environments/environment';
import {
  CVCValidator,
  CreditCardService,
  CreditCardValidator, ExpirationDateValidator,
  NonEmptyValidator,
  Validated
} from 'validation';

@Page('payment')
@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PaymentComponent extends PageComponent implements FormHandlingComponent {
  private stripe = inject(StripeService);
  private creditCards = inject(CreditCardService);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private zone = inject(NgZone);
  private redirection = inject(Redirection);
  private userApi = inject(UsersApi);
  private offersApi = inject(OffersApi);

  get pageComponentId() {
    return 'payment';
  }

  get backgroundImage() {
    return this.currentBackgroundImage;
  }


  @Transfer()
  currentBackgroundImage: string;
  @Transfer()
  offerSet : ApiOfferSet;

  offer: ApiOffer;
  membershipProduct: ApiMembershipProduct;
  offerSetUUID: string;
  offerUUID: string = null;
  submitting: boolean = false;
  cardBrand: string = null;
  cardBrandIcon: string = null;
  validityError: boolean;
  gatewayName: string = 'stripe';
  applePay: boolean = false;
  applePayInProgress: boolean = false;

  @Validated({ class: NonEmptyValidator, field: 'cardholder-name' })
  @FormInput()
  cardholderName: string = "";

  @Validated({ class: CreditCardValidator, field: 'cc-number' })
  @FormInput()
  creditCard: string = "";

  @Validated({ class: ExpirationDateValidator, field: 'cc-exp' })
  @FormInput()
  expiration: string = "";

  @Validated({ class: CVCValidator, field: 'cc-csc' })
  @FormInput()
  cvc: string = "";

  @FormInput()
  address_zip: string = "";

  userLoaded : boolean = false;
  user: ApiUser;
  clientLoaded = false;

  async init() {

    // If we are on the client side, check if Apple Pay is available on this browser.
    if (typeof window !== 'undefined') {
      this.clientLoaded = true;
      await this.stripe.apiReady();
      this.stripe.applePay.checkAvailability(available => {
        if (available) {
          this.applePay = true;
        }
      });
    }

    this.subscribe(this.userService.userChanged, user => {
      this.user = user;
      if (!this.submitting && this.user && this.user.membership && this.user.membership.active && this.user.membership.gateway_name != 'free') {
        this.redirection.go('/membership/change');
        return;
      }

      this.userLoaded = true;
      this.ensureUserLoggedIn();
    });

    this.subscribe(this.route.paramMap, async params => {
      this.offerUUID = params.get('offerUUID');
      this.offerSetUUID = params.get('offerSetUUID');

      if (this.offerUUID == 'null')
        this.offerUUID = null;
      if (this.offerSetUUID == 'null')
        this.offerSetUUID = null;

      if (this.offerUUID == null || this.offerSetUUID == null) {
        this.redirection.go('/join');
        return;
      }

      try {
        this.offer = await this.offersApi.get(this.offerUUID).toPromise();
        this.membershipProduct = this.offer.membership_product;

        if (this.offer) {
          this.setBackgroundImage();
        }
      } catch(e) {
        // offer doesn't exist so route them back to offer page
        this.redirection.go('/join');
      }

      if (!this.offerUUID)
        return;

      this.ensureUserLoggedIn();
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        this.setBackgroundImage();
      });
    }
  }

  async setBackgroundImage() {
    if (!this.offerSet)
      return;
    
    if (typeof window === 'undefined') {
      this.currentBackgroundImage = this.offerSet.desktop_image;
      return;
    }

    if (window.innerWidth <= 580) {
      this.currentBackgroundImage = this.offerSet.mobile_image;
    } else if (window.innerWidth > 580 && window.innerWidth < 921) {
      this.currentBackgroundImage = this.offerSet.tablet_image;
    } else {
      this.currentBackgroundImage = this.offerSet.desktop_image;
    }
  }

  ensureUserLoggedIn() {
    if (!this.offerUUID)
      return;
    if (!this.userLoaded)
      return;
    if (this.user)
      return;

    this.redirection.go(`/signup/${this.offerSetUUID}/${this.offerUUID}`);
  }

  togglePaymentType() {
    this.applePay = !this.applePay;
  }
  goBack() {
    // this.location.back();
    this.redirection.go('/profile');
  }

  @ViewChild('form')
  form: FormComponent;

  submit() {
    if (!this.form.validate())
      return;

    this.purchase();
  }

  async createMembership(token : string, cb) {
    let membership: ApiMembership;
    try {
      let annual = this.offer.period === "anually";
      let dollarPrice = this.membershipProduct ? (this.membershipProduct.price / 100) : 0;
      let productID = this.membershipProduct ? this.membershipProduct.id : 0;
      let productSKU = this.membershipProduct ? this.membershipProduct.product_sku : 'unknown';

      membership = await this.userApi.createMembership({
        offer_set_uuid: this.offerSetUUID,
        offer_uuid: this.offerUUID,
        annual,
        gateway_name: this.gatewayName,
        gateway_data: <any>{
          token
        },
        source: this.userService.source,
        source_first_time: this.userService.sourceFirstTime
      }).toPromise();

      this.user.membership = membership;
      this.userService.user = this.user;

      let beaconsEnabled = environment.settings.enableBeacons || window['force_beacons'] === 1;
      if (beaconsEnabled) {
        this.userService.sendBeaconGA4('purchase', {
          purchase_type: 'membership',
          offer_presentation: 'page',
          app: 'tyt_accounts',
          path: 'offers_v1',
          offers_version: 1,
          offer_set: this.offerSetUUID,
          offer: this.offerUUID,
          membership_product_id: membership.product.id,
          membership_product_sku: membership.product.product_sku,
          transaction_id: membership.uuid,
          currency: 'USD',
          value: membership.product.price / 100.0,
          items: [{
            item_id: membership.product.product_sku,
            item_name: membership.product.name,
            item_brand: 'TYT',
            item_category: 'Membership',
            price: membership.product.price / 100.0,
            quantity: 1
          }]
        });

        // Let our Facebook Pixel(s) know...
        if (typeof window['xfbq'] !== 'undefined') {
          let xfbq = window['xfbq'];
          xfbq(
            'track', 'Purchase',
            {
              value: dollarPrice,
              currency: 'USD',
              contents: [
                {
                  id: productID,
                  quantity: 1
                }
              ],
              purchase_type: 'membership',
              offer_set_id: this.offerSetUUID,
              offer_id: this.offerUUID,
              membership_product_sku: productSKU,
              membership_product_id: productID,
              content_type: 'product'
            }
          );
        }
      }
      
      // Put in a generous delay to ensure that all pending analytics messages 
      // are recorded properly (we will redirect off the web app once this 
      // process completes, so this is important)
      
      setTimeout(() => cb(), 5000);
    } catch (e) {
      if (e.json)
        e = e.json();

      console.error('Caught exception while creating membership');
      console.error(e);

      this.showError(e.message || e.error);
      setTimeout(() => this.submitting = false, 3000);
    }
  }

  async purchaseWithApplePay() {

    if (typeof window === 'undefined') {
      console.error('Cannot do Apple Pay purchase on the server-side!');
      return;
    }

    let label = `${this.offer.title}`;
    // this.submitting = true;
    this.applePayInProgress = true;

    let session = this.stripe.applePay.buildSession({
      total: {
        label,
        amount: this.offer.dollar_price
      },
      countryCode: 'US',
      currencyCode: 'USD'

    },
    (result, completion) => {
      let token = result.token.id;

      try {
        this.createMembership(token, async () => {
          completion(true);
          this.redirectToPaymentConfirmation();
        });
      } catch (e) {
        console.error('Caught exception while creating membership:');
        console.error(e);
        completion(false);
        this.showError(e.message || e.error || 'An error occurred while processing your request.');
        setTimeout(() => this.submitting = false, 3000);
      }
    },
    (e) => {
      console.error('Error caught during Apple Pay buildSession():');
      console.error(e);
      // this.submitting = false;
      this.applePayInProgress = false;
      this.showError(e.message);
    });

    session.oncancel = () => {
      this.zone.run(() => {
        // User closed the Apple Pay session
        setTimeout(() => {
          this.applePayInProgress = false;
        });
      });
    };

    session.begin();
  }

  async purchase() {

    /*** CRITICAL SECTION: NO ASYNC HERE */
    if (this.submitting)
      return;
    this.submitting = true;
    /*** CRITICAL SECTION: NO ASYNC HERE */

    if (this.expiration)
      this.expiration = this.expiration.replace(/-/, '/');

    try {
      this.user = <any>await this.userApi.getCurrentUser().toPromise();
    } catch (e) {
      if (e.json)
        e = e.json();

      this.userService.goToLogin();
      return;
    }

    if (this.user.membership && this.user.membership.active && this.user.membership.gateway_name != 'free') {
      this.showError("You currently have an active membership");
      return;
    }

    if (typeof window === 'undefined') {
      console.error('Cannot do purchase server-side!');
      return;
    }

    this.fieldErrors = null;
    this.error = this.validityError = false;

    let token: CreateStripeTokenResponse;
    let cardData : any = {
      name: this.cardholderName,
      number: this.creditCard,
      exp: this.expiration,
      cvc: this.cvc
    };

    if (this.address_zip && this.address_zip != '')
      cardData.address_zip = this.address_zip;

    try {
      token = await this.stripe.createToken(cardData);
    } catch (e) {
      this.showError(e.message || e.error);
      setTimeout(() => this.submitting = false, 3000);
      return;
    }

    if (token.response.error && token.response.error.message) {
      this.showError(token.response.error.message);
      setTimeout(() => this.submitting = false, 3000);
      return;
    }

    this.createMembership(token.response.id, async () => {
      this.redirectToPaymentConfirmation();
    });
  }

  async getGoogleAnalyticsLinkerParam() {
    let clientId = null;

    if (typeof window !== 'undefined' && window['ga']) {
      await new Promise<void>((res, rej) => {
        window['ga'](function(tracker) {
          clientId = tracker.get('linkerParam');
          res();
        });
      });
    }

    return clientId;
  }

  async redirectToPaymentConfirmation() {
    let offerCombination = this.offerSetUUID ? `${this.offerSetUUID}/${this.offerUUID}` : '';
    let query = `converted=1`;
    
    if (this.offer && this.offer.membership_product) {
      query += `&membership_product_id=${this.offer.membership_product.id}`;
    }

    let gaLinkerParam = await this.getGoogleAnalyticsLinkerParam();
    query += `&${gaLinkerParam}`;

    this.redirection.go(`${environment.urls.app}/payment-confirmation/${offerCombination}?${query}`);
  }

  updateCardBrand() {
    setTimeout(() => {
      let cardStr = this.creditCard;
      let cardInfo = this.creditCards.getInfo(cardStr);
      this.cardBrand = cardInfo && cardInfo.card ? cardInfo.card.type : null;

      this.cardBrandIcon = {
        'visa': 'cc-visa',
        'master-card': 'cc-mastercard',
        'discover': 'cc-discover',
        'american-express': 'cc-amex',
        'jcb': 'cc-jcb',
        'diners-club': 'cc-diners-club',
      }[this.cardBrand];
    }, 100);
  }

  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

}
